import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Card,
  CardContent,
  Grid,
  Avatar
} from '@mui/material';
import {
  Save as SaveIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  AttachMoney as PriceIcon,
  Public as CountryIcon,
  LocationCity as CityIcon,
  DateRange as DateRangeIcon,
  Tune as DetailsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../context/CategoryContext';
import { useCountries } from '../../hooks/useFilterData';
import savedSearchService from '../../services/savedSearchService';
import { useCategoryName } from '../../utils/categoryTranslation';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
const getUploadedFileUrl = (path) => {
  if (!path) return null;
  if (typeof path !== 'string') return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return `${UPLOADS_BASE_URL}${path}`;
  return `${UPLOADS_BASE_URL}/${path}`;
};

/**
 * 💾 MODAL DE SAUVEGARDE DE RECHERCHE
 * 
 * Fonctionnalités:
 * ✅ Saisie du nom de la recherche
 * ✅ Description optionnelle
 * ✅ Définition comme recherche par défaut
 * ✅ Aperçu des filtres actifs
 * ✅ Validation et gestion d'erreurs
 */
const SaveSearchModal = ({ 
  open, 
  onClose, 
  filters = {}, 
  onSaved 
}) => {
  const { t, i18n } = useTranslation(['common', 'listingFields']);
  const { categoriesList } = useCategories();
  const { data: countries } = useCountries();
  const getCategoryName = useCategoryName();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isDefault: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 🎯 FONCTION POUR FORMATER LES DÉTAILS DES FILTRES
  const getFilterDetails = () => {
    const details = {
      location: {},
      categories: [],
      price: {},
      other: {}
    };

    // 🌍 LOCALISATION
    if (filters.country) {
      const country = countries.find(c => c.code === filters.country);
      details.location.country = country ? country.name : filters.country;
    }
    
    if (filters.cities && Array.isArray(filters.cities) && filters.cities.length > 0) {
      details.location.cities = filters.cities;
    }

    // 🏷️ CATÉGORIES
    if (filters.categoryIds && Array.isArray(filters.categoryIds) && filters.categoryIds.length > 0) {
      details.categories = filters.categoryIds
        .map(categoryId => {
          return categoriesList.find(cat => cat.id === categoryId) || null;
        })
        .filter(Boolean);
    }

    // 💰 PRIX
    if (filters.priceType) {
      details.price.type = filters.priceType;
    }
    if (filters.minPrice && Number(filters.minPrice) > 0) {
      details.price.min = filters.minPrice;
    }
    if (filters.maxPrice && Number(filters.maxPrice) > 0) {
      details.price.max = filters.maxPrice;
    }
    if (filters.currency) {
      details.price.currency = filters.currency;
    }

    // � DISPONIBILITÉ - NOUVELLES DATES
    if (filters.startDate || filters.endDate || filters.immediateOnly || filters.availabilityDuration) {
      details.availability = {};
      
      if (filters.startDate) {
        details.availability.startDate = filters.startDate;
      }
      if (filters.endDate) {
        details.availability.endDate = filters.endDate;
      }
      if (filters.immediateOnly) {
        details.availability.immediate = filters.immediateOnly;
      }
      if (filters.availabilityDuration) {
        details.availability.duration = filters.availabilityDuration;
      }
    }
    // 🔍 FILTRES DÉTAILS (details_*)
    const detailsEntries = Object.entries(filters || {}).filter(([key, value]) =>
      key.startsWith('details_') &&
      value !== null && value !== undefined && value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    );
    if (detailsEntries.length > 0) {
      details.details = detailsEntries.map(([key, value]) => ({
        key: key.replace(/^details_/, '').replace(/_/g, ' '),
        value
      }));
    }
    // �🔍 AUTRES FILTRES
    if (filters.isAvailable !== undefined) {
      details.other.availability = filters.isAvailable ? 'savedSearches.availableOnly' : 'savedSearches.includeUnavailable';
    }
    if (filters.isVerified !== undefined) {
      details.other.verification = filters.isVerified ? 'savedSearches.verifiedOnly' : 'savedSearches.includeUnverified';
    }

    return details;
  };

  const filterDetails = getFilterDetails();

  // 📊 Calculer les filtres actifs pour l'aperçu
  const activeFiltersCount = Object.entries(filters || {}).filter(([key, value]) => {
    return value !== null && value !== undefined && value !== '' && 
           !(Array.isArray(value) && value.length === 0) &&
           !key.startsWith('_'); // Exclure les métadonnées
  }).length;

  // 🎯 Gérer les changements de formulaire
  const handleInputChange = (field) => (event) => {
    const value = field === 'isDefault' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null); // Effacer l'erreur lors de la saisie
  };

  // 💾 Sauvegarder la recherche
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.name.trim()) {
        setError('Le nom de la recherche est requis');
        return;
      }

      if (activeFiltersCount === 0) {
        setError(t('savedSearches.errors.noActiveFilters', 'Aucun filtre actif à sauvegarder'));
        return;
      }

      console.log('💾 [SaveSearchModal] Saving search:', {
        name: formData.name,
        filtersCount: activeFiltersCount,
        isDefault: formData.isDefault
      });

      const result = await savedSearchService.saveSearch({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        filters,
        isDefault: formData.isDefault
      });

      console.log('✅ [SaveSearchModal] Search saved successfully:', result);

      // Notifier le parent
      onSaved?.(result.data);

      // Réinitialiser et fermer
      setFormData({ name: '', description: '', isDefault: false });
      onClose();

    } catch (error) {
      console.error('❌ [SaveSearchModal] Save error:', error);
      setError(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // 🚪 Gérer la fermeture
  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', description: '', isDefault: false });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
        color: 'white',
        borderRadius: '8px 8px 0 0'
      }}>
        <SaveIcon />
        {t('savedSearches.saveThisSearch', 'Sauvegarder cette recherche')}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* 📊 APERÇU DÉTAILLÉ DES FILTRES */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon color="primary" />
            {t('savedSearches.filtersToSave', 'Filtres qui seront sauvegardés')}
          </Typography>
          
          {activeFiltersCount > 0 ? (
            <Grid container spacing={2}>
              {/* 🌍 LOCALISATION */}
              {(filterDetails.location.country || filterDetails.location.cities?.length > 0) && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'rgba(79, 172, 254, 0.05)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationIcon color="primary" />
                        {t('filters.location.title', 'Localisation')}
                      </Typography>
                      
                      {filterDetails.location.country && (
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            icon={<CountryIcon />}
                            label={`${t('savedSearches.countryLabel', 'Pays')}: ${filterDetails.location.country}`}
                            variant="outlined"
                            size="small"
                            sx={{ mr: 1, mb: 0.5 }}
                          />
                        </Box>
                      )}
                      
                      {filterDetails.location.cities?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {t('filters.location.cities', 'Villes')} ({filterDetails.location.cities.length}) :
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {filterDetails.location.cities.map((city, index) => (
                              <Chip
                                key={index}
                                icon={<CityIcon />}
                                label={city}
                                variant="outlined"
                                size="small"
                                color="primary"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* 🏷️ CATÉGORIES */}
              {filterDetails.categories.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CategoryIcon color="secondary" />
                        {t('filters.categories.title', 'Catégories')} ({filterDetails.categories.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {filterDetails.categories.map((category, index) => {
                          const imgUrl = getUploadedFileUrl(category.imageUrl);
                          return (
                            <Chip
                              key={index}
                              label={getCategoryName(category)}
                              avatar={imgUrl ? <Avatar src={imgUrl} alt={getCategoryName(category)} sx={{ width: 20, height: 20 }} /> : undefined}
                              variant={imgUrl ? 'outlined' : 'filled'}
                              size="small"
                              color="secondary"
                            />
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* 💰 PRIX */}
              {(filterDetails.price.type || filterDetails.price.min || filterDetails.price.max) && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PriceIcon color="success" />
                        {t('filters.price.title', 'Prix')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {filterDetails.price.type && (
                          <Chip
                            label={`${t('savedSearches.typeLabel', 'Type')}: ${t(`filters.priceType.${filterDetails.price.type}`, filterDetails.price.type)}`}
                            variant="outlined"
                            size="small"
                            color="success"
                          />
                        )}
                        
                        {filterDetails.price.min && (
                          <Chip
                            label={`${t('savedSearches.minLabel', 'Min')}: ${filterDetails.price.min}${filterDetails.price.currency || '€'}`}
                            variant="outlined"
                            size="small"
                            color="success"
                          />
                        )}
                        
                        {filterDetails.price.max && (
                          <Chip
                            label={`${t('savedSearches.maxLabel', 'Max')}: ${filterDetails.price.max}${filterDetails.price.currency || '€'}`}
                            variant="outlined"
                            size="small"
                            color="success"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* � DISPONIBILITÉ - DATES */}
              {filterDetails.availability && (
                <Grid item xs={12}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      bgcolor: 'rgba(255, 152, 0, 0.08)', 
                      border: '2px solid #ff9800',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #ff9800, #f57c00)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2, pt: 3 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mb: 2, 
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: '#e65100'
                        }}
                      >
                        <DateRangeIcon sx={{ color: '#ff9800', fontSize: '1.3rem' }} />
                        {t('availability.title', 'Période de disponibilité')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {filterDetails.availability.startDate && (
                          <Chip
                            label={`📅 ${t('savedSearches.fromDate', 'Du')}: ${new Date(filterDetails.availability.startDate).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' })}`}
                            variant="filled"
                            size="medium"
                            sx={{ 
                              backgroundColor: '#ff9800', 
                              color: 'white', 
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              height: '32px',
                              '& .MuiChip-label': { 
                                fontSize: '0.85rem',
                                fontWeight: 700
                              },
                              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                            }}
                          />
                        )}
                        
                        {filterDetails.availability.endDate && (
                          <Chip
                            label={`📅 ${t('savedSearches.toDate', 'Au')}: ${new Date(filterDetails.availability.endDate).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' })}`}
                            variant="filled"
                            size="medium"
                            sx={{ 
                              backgroundColor: '#f57c00', 
                              color: 'white', 
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              height: '32px',
                              '& .MuiChip-label': { 
                                fontSize: '0.85rem',
                                fontWeight: 700
                              },
                              boxShadow: '0 2px 8px rgba(245, 124, 0, 0.3)'
                            }}
                          />
                        )}
                        
                        {filterDetails.availability.immediate && (
                          <Chip
                            label={`⚡ ${t('availability.immediateOnly', 'Disponible maintenant')}`}
                            variant="filled"
                            size="medium"
                            sx={{ 
                              backgroundColor: '#4caf50', 
                              color: 'white', 
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              height: '32px',
                              '& .MuiChip-label': { 
                                fontSize: '0.85rem',
                                fontWeight: 700
                              },
                              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                            }}
                          />
                        )}
                        
                        {filterDetails.availability.duration && (
                          <Chip
                            label={`⏱️ ${t('savedSearches.durationDays', '{{days}} jours', { days: filterDetails.availability.duration })}`}
                            variant="outlined"
                            size="medium"
                            sx={{ 
                              color: '#e65100', 
                              borderColor: '#ff9800',
                              borderWidth: '2px',
                              fontWeight: 600,
                              height: '32px',
                              '& .MuiChip-label': { 
                                fontSize: '0.85rem',
                                fontWeight: 600
                              }
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {/* 🔍 FILTRES DÉTAILS */}
              {filterDetails.details?.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'rgba(0, 150, 136, 0.05)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <DetailsIcon sx={{ color: '#009688' }} />
                        {t('savedSearches.filterDetails', 'Détails spécifiques')} ({filterDetails.details.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {filterDetails.details.map(({ key, value }, idx) => {
                          const translatedKey = t(`listingFields:${key}`, key);
                          const translatedValue = Array.isArray(value)
                            ? value.map(v => t(`listingFields:opt_${v}`, String(v))).join(', ')
                            : t(`listingFields:opt_${value}`, String(value));
                          return (
                            <Chip
                              key={idx}
                              label={`${translatedKey}: ${translatedValue}`}
                              variant="outlined"
                              size="small"
                              sx={{ color: '#009688', borderColor: '#009688' }}
                            />
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {/* �🔍 AUTRES FILTRES */}
              {(filterDetails.other.availability || filterDetails.other.verification) && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'rgba(156, 39, 176, 0.05)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <StarIcon sx={{ color: '#9c27b0' }} />
                        {t('filters.other.title', 'Autres critères')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {filterDetails.other.availability && (
                          <Chip
                            label={t(filterDetails.other.availability, filterDetails.other.availability)}
                            variant="outlined"
                            size="small"
                            sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}
                          />
                        )}
                        
                        {filterDetails.other.verification && (
                          <Chip
                            label={t(filterDetails.other.verification, filterDetails.other.verification)}
                            variant="outlined"
                            size="small"
                            sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Alert severity="warning" variant="outlined">
              {t('savedSearches.noActiveFilters', 'Aucun filtre actif. Veuillez sélectionner des filtres avant de sauvegarder.')}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ✏️ Formulaire */}
        <Stack spacing={3}>
          {/* Nom de la recherche */}
          <TextField
            label={t('savedSearches.searchName', 'Nom de la recherche *')}
            value={formData.name}
            onChange={handleInputChange('name')}
            fullWidth
            placeholder={t('savedSearches.searchNamePlaceholder', 'Ex : Appartements Paris centre')}
            variant="outlined"
            disabled={loading}
            inputProps={{ maxLength: 255 }}
            helperText={`${formData.name.length}/255 ${t('common.characters', 'caractères')}`}
          />

          {/* Description optionnelle */}
          <TextField
            label={t('savedSearches.descriptionLabel', 'Description (optionnelle)')}
            value={formData.description}
            onChange={handleInputChange('description')}
            fullWidth
            multiline
            rows={2}
            placeholder={t('savedSearches.descriptionPlaceholder', 'Décrivez cette recherche...')}
            variant="outlined"
            disabled={loading}
            inputProps={{ maxLength: 500 }}
            helperText={`${formData.description.length}/500 ${t('common.characters', 'caractères')}`}
          />

          {/* Recherche par défaut */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isDefault}
                onChange={handleInputChange('isDefault')}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon fontSize="small" color={formData.isDefault ? 'primary' : 'disabled'} />
                {t('savedSearches.setAsDefault', 'Définir comme recherche par défaut')}
              </Box>
            }
          />

          {formData.isDefault && (
            <Alert severity="info" variant="outlined">
              {t('savedSearches.setAsDefaultHint', 'Cette recherche sera automatiquement appliquée lors de vos prochaines connexions.')}
            </Alert>
          )}
        </Stack>

        {/* ❌ Erreur */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          {t('savedSearches.cancel', 'Annuler')}
        </Button>
        
        <Button 
          onClick={handleSave}
          disabled={loading || !formData.name.trim() || activeFiltersCount === 0}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #7c4dff 30%, #6200ee 90%)'
            }
          }}
        >
          {loading ? t('savedSearches.saving', 'Sauvegarde...') : t('savedSearches.save', 'Sauvegarder')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveSearchModal;
